import React from "react";
import moment from "moment";
import { Badge, Textarea, TextInput } from "@mantine/core";
import { DatePicker } from "@mantine/dates";
import { IconPencil, IconTrash } from "@tabler/icons";
import {
  PageList,
  ENUM_FORM_DISPLAY,
  ENUM_FORM_LAYOUT_CONTAINER,
} from "../../components/pageList";
import ActionCellRender from "../../components/actionCellRender";
import { mcaListColumn } from "../../components/mca/McaEntityIndicators";

const apiEntity = "approval";

const APPROVAL_STATUS_COLORS = {
  DFT: "gray",
  ACTIVE: "green",
  INACTIVE: "red",
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD") : "");

const formatEffectivePeriod = (data) => {
  const from = data.effectiveFrom;
  const to = data.effectiveTo;
  if (!from && !to) return "--";
  if (from && to) return `${formatDate(from)} – ${formatDate(to)}`;
  return formatDate(from || to);
};

const formSchema = {
  title: "Approval",
  description: "Manage business approval records.",
  isMainForm: true,
  display: {
    mode: ENUM_FORM_DISPLAY.MODAL,
    size: "lg",
    position: "right",
  },
  closeAfterSave: true,
  apiEntity,
  initialValues: {
    title: "",
    description: "",
    category: "",
    effectiveFrom: null,
    effectiveTo: null,
  },
  validate: {
    title: (value) =>
      String(value ?? "").trim() ? null : "Title is required",
  },
  layout: {
    containers: [
      {
        key: "main-grid",
        parent: null,
        type: ENUM_FORM_LAYOUT_CONTAINER.GRID,
        cols: [
          { key: "col-left", props: { span: 6 } },
          { key: "col-right", props: { span: 6 } },
        ],
      },
    ],
    fields: [
      {
        name: "title",
        parent: "col-left",
        component: TextInput,
        props: {
          label: "Title",
          required: true,
        },
      },
      {
        name: "category",
        parent: "col-right",
        component: TextInput,
        props: {
          label: "Category",
          placeholder: "e.g. POLICY, FUND, GENERAL",
        },
      },
      {
        name: "effectiveFrom",
        parent: "col-left",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective From",
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "effectiveTo",
        parent: "col-right",
        component: DatePicker,
        type: "datePicker",
        props: {
          label: "Effective To",
          allowFreeInput: true,
          zIndex: 1000,
        },
      },
      {
        name: "description",
        parent: "main-grid",
        component: Textarea,
        props: {
          label: "Description",
          minRows: 3,
        },
      },
    ],
  },
};

const ApprovalPage = () => {
  const tableSchema = {
    showActionColumn: true,
    columns: [
      {
        field: "code",
        headerName: "Code",
        sortable: true,
      },
      {
        field: "title",
        headerName: "Title",
        sortable: true,
      },
      {
        field: "category",
        headerName: "Category",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        cellRender: (_col, data) => (
          <Badge color={APPROVAL_STATUS_COLORS[data.status] ?? "gray"}>
            {data.status ?? "—"}
          </Badge>
        ),
      },
      {
        field: "effectiveFrom",
        headerName: "Effective Period",
        sortable: true,
        cellRender: (_col, data) => formatEffectivePeriod(data),
      },
      mcaListColumn(),
      {
        field: "action",
        cellRender: ActionCellRender,
        isAction: true,
        actions: [{ name: "edit", icon: IconPencil }],
        actionMenu: [{ name: "delete", icon: IconTrash }],
      },
    ],
  };

  return (
    <PageList
      title="Approvals"
      apiEntity={apiEntity}
      tableSchema={tableSchema}
      formSchema={{
        add: {
          ...formSchema,
          title: "Add Approval",
        },
        edit: {
          ...formSchema,
          title: "Edit Approval",
        },
      }}
      defaultSort={{ field: "createdAt", order: "desc" }}
      selectFields="code title description category status effectiveFrom effectiveTo mcaPending mcaRequestId mcaRequestId.code mcaRequestId.status mcaRequestId.action mcaRequestId.process mcaRequestId.process.name createdAt"
    />
  );
};

export default ApprovalPage;
